import { useEffect, useState } from 'react';
import { useUserStrategies } from '../../../context/UserStrategies';
import { aggregateGroups, getSymbol } from '../../../helpers/functions';
import { DropdownMenu } from '../../form';
import { TTickerCurrency } from '../../../types/model';
import { Center, StrategyAssetsGroup, PieData } from '../types';
import { tableColors } from '../WidgetWithPie';
import { Pie } from '../WidgetWithPie/Pie';
import { GroupsList } from '../WidgetWithPie/GroupsList';
import { COLUMNS_STRATEGY_PERFORMANCE } from './StrategiesAllocation';
import { StrategyLabel } from '../../Strategies/StrategyLabel';
import { CHART_COLORS_HEX } from '../../../constants/colors';
import { StrategyAssetsList } from '../WidgetWithPie/StrategyAssetsList';

export const PieAndTable = ({
  groupBy,
  selectedId,
  selectedStrategies,
  handleSelectedStrategies,
  width,
}: {
  groupBy: 'STRATEGIES' | 'SOURCE' | 'INSPIRATION';
  selectedId: string;
  selectedStrategies: string[];
  handleSelectedStrategies: (id: string) => void;
  width: number;
}) => {
  const { strategies } = useUserStrategies();
  const {
    color,
    currency,
    name,
    strategyAssets: { items },
  } = strategies.find(({ id }) => id === selectedId)!;

  const { dropdownItems, dropdownItemsData } = strategies
    .filter(({ id }) => !selectedStrategies.includes(id))
    .reduce(
      ({ dropdownItems, dropdownItemsData }, { id, name }) => {
        dropdownItems.push(name);
        dropdownItemsData.push(id);

        return { dropdownItems, dropdownItemsData };
      },
      {
        dropdownItems: [] as string[],
        dropdownItemsData: [] as string[],
      }
    );

  const strategyValue = items.reduce(
    (total, { unit, asset: { history } }) =>
      total +
      unit * history[0][currency.toLocaleLowerCase() as TTickerCurrency],
    0
  );

  const [center, setCenter] = useState<Center>({
    id: 'Total',
    value: strategyValue,
  });
  const [groups, setGroups] = useState<StrategyAssetsGroup[]>([]);
  const [pieData, setPieData] = useState<PieData[]>([]);

  const resetCenter = (value?: number) => {
    setCenter({ value: value ?? strategyValue, id: 'Total' });
  };

  const symbol = getSymbol(currency);
  const handlePieCenter = (i: number) => {
    let idx = groups.findIndex(({ title }) => title === center.id);

    !(idx + i) || idx + i === groups.length
      ? (i = 0)
      : idx + i < 0
      ? (i = groups.length - 1)
      : (i = idx + i);

    const id = groups[i].title;
    const value = groups[i].totalValue;

    setCenter({ id, value });
  };

  useEffect(() => {
    const newGroups = aggregateGroups({ assets: items, currency, groupBy }).map(
      (obj, i) => ({
        ...obj,
        color: tableColors[i],
        currency,
        title: obj.title,
      })
    );

    setGroups(newGroups);
    setPieData(
      newGroups.map(({ title: id, totalValue: value, color }) => ({
        id,
        value,
        color: CHART_COLORS_HEX[color],
      }))
    );
    resetCenter();
  }, [items, groupBy]);

  const [selectedStrategy, setSelectedStrategy] =
    useState<StrategyAssetsGroup>();
  const handleSelectStrategy = () => {
    const group = groups.find(({ title }) => title === center.id);

    setSelectedStrategy(group);
  };

  useEffect(() => {
    setSelectedStrategy(undefined);
  }, [groupBy]);

  return (
    <div
      key={selectedId}
      className={`
        flex
        ${width > 1 ? 'w-1/2' : 'w-full'}
        flex-col
      `}
    >
      <span className="flex py-3 border-b border-grey-10 dark:border-grey-70">
        <DropdownMenu
          dropdownClassName={`
            flex
            items-center
          `}
          dropdownTooltipClassName={`
            bg-white
            border-solid
            border
            border-grey-30
          `}
          showArrow={dropdownItems.length !== 0}
          dropdownArrowClassName=""
          dropdownTooltipXOffset={-10}
          dropdownTooltipYOffset={10}
          dropdownItems={dropdownItems}
          dropdownItemsData={dropdownItemsData}
          handleClick={(name, id) => handleSelectedStrategies(id)}
        >
          <StrategyLabel
            color={color}
            name={name}
            className="ml-3 -mr-7 pr-7"
          />
        </DropdownMenu>
      </span>

      {/* TABLE */}
      <div
        className={`
          flex
          ${width < 4 && 'flex-col'}
          h-auto
          divide-x
          divide-grey-10
          dark:divide-grey-70
        `}
      >
        {/* GROUPS LIST */}

        <div className={`flex w-full  ${width < 4 && 'flex-col'}`}>
          {/* CHART */}
          <Pie
            center={center}
            className={`
                flex-none
                mx-auto
                w-96
                h-96
                relative
                top-0
                left-0
                ${width > 3 && 'border border-grey-5'}
              `}
            margin={50}
            symbol={symbol}
            strategyValue={strategyValue}
            resetCenter={resetCenter}
            setCenter={setCenter}
            pieData={pieData}
            handlePieCenter={handlePieCenter}
          />

          {selectedStrategy ? (
            <StrategyAssetsList
              column
              strategy={selectedStrategy}
              strategyValue={strategyValue}
              widgetWidth={2}
              onClickArrowBack={() => setSelectedStrategy(undefined)}
            />
          ) : (
            <div className=" h-full w-full">
              <GroupsList
                columns={[...COLUMNS_STRATEGY_PERFORMANCE]}
                column={width < 3}
                fitMinWidth
                groupBy={groupBy}
                center={center}
                groups={groups}
                interval="VAR_1Y"
                symbol={symbol}
                strategyValue={strategyValue}
                widgetWidth={1}
                resetCenter={resetCenter}
                setCenter={setCenter}
                handleSelectStrategy={handleSelectStrategy}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
